import { LabelFormFieldSkeleton } from "@/admin/labels/LabelFormField"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabel } from "@/core/context/LabelsContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import ConnectStripeBanner from "@/payment/stripe-integration/banner/ConnectStripeBanner"
import PricingInput from "@/pricing/input/PricingInput"
import { ExperienceSettingsFormStore } from "@/pricing/PricingFormFields"
import { decimalize } from "@/pricing/pricingUtils"
import ExperienceSettingsLabel from "@/product/settings/ExperienceSettingsLabel"
import {
  useExperienceSettingsSelectedPlans_membershipPlansFragment$data,
  useExperienceSettingsSelectedPlans_membershipPlansFragment$key,
} from "@/product/settings/hooks/__generated__/useExperienceSettingsSelectedPlans_membershipPlansFragment.graphql"
import useExperienceSettingsSelectedPlans from "@/product/settings/hooks/useExperienceSettingsSelectedPlans"
import MembershipPlanExperiencePricingListItem from "@/product/settings/pricing/MembershipPlanExperiencePricingListItem"
import { NodeFromConnection } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoChip,
  DiscoDivider,
  DiscoFormControl,
  DiscoLink,
  DiscoSection,
  DiscoSwitch,
  DiscoText,
} from "@disco-ui"
import { TestIDProps } from "@utils/typeUtils"
import { observable } from "mobx"
import { observer } from "mobx-react-lite"
import pluralize from "pluralize"
import { Fragment, useEffect, useRef } from "react"
import { generatePath } from "react-router-dom"

type Plan = NodeFromConnection<
  NonNullable<useExperienceSettingsSelectedPlans_membershipPlansFragment$data>["membershipPlans"]
>

interface Props extends TestIDProps {
  form: ExperienceSettingsFormStore
  organizationKey: useExperienceSettingsSelectedPlans_membershipPlansFragment$key
  hidePricing?: boolean
}

function ExperiencePricingFormFields({
  testid = "PricingFormFields",
  form,
  organizationKey,
  hidePricing,
}: Props) {
  const experienceLabel = useLabel("admin_experience")
  const membersLabel = useLabel("admin_member")
  const activeOrganization = useActiveOrganization()!
  const isStripeConnected = Boolean(activeOrganization.stripeAccountId)
  const classes = useStyles()
  const existingBenefits = form.state.benefits
  const existingBenefitsRef = useRef(existingBenefits)

  // When the form is submitted, reset the existing benefits
  useEffect(() => {
    existingBenefitsRef.current = form.initialState.benefits
  }, [form.initialState.benefits])

  const { hasCustomPlans, availableMembershipPlans } = useExperienceSettingsSelectedPlans(
    {
      organizationKey,
      benefits: form.state.benefits,
    }
  )

  return (
    <>
      {!hidePricing && (
        <DiscoFormControl>
          <DiscoSwitch
            checked={form.state.isPaid}
            onChange={handleToggleIsPaid}
            testid={`${testid}.paid-fee-switch`}
            label={
              <ExperienceSettingsLabel
                title={"One-time Fee"}
                sectionId={"experience_paid_fee"}
                tooltipContent={`Grant access to a ${experienceLabel.singular} by charging a one-time fee, through a membership plan, or by using a combination of both.`}
              />
            }
            sublabel={
              <DiscoText variant={"body-sm"} marginBottom={1.5}>
                {`Charge a one-off access fee for this ${experienceLabel.singular}.`}
              </DiscoText>
            }
          />
          {form.state.isPaid &&
            (isStripeConnected ? (
              <DiscoSection className={classes.extraCharge}>
                <PricingInput
                  fullWidth
                  data-testid={`${testid}.price`}
                  value={form.state.price}
                  disabled={!form.state.isPaid}
                  onChange={handleUpdateExtraCharge}
                />
              </DiscoSection>
            ) : (
              <ConnectStripeBanner
                testid={testid}
                details={`Stripe must be connected to create a paid ${experienceLabel.singular}.`}
              />
            ))}
        </DiscoFormControl>
      )}

      {hasCustomPlans && (
        <DiscoFormControl
          label={
            <ExperienceSettingsLabel
              title={"Membership Plans"}
              sectionId={"experience_membership_plans"}
              tooltipContent={`Only ${membersLabel.plural} on the selected membership plan(s) can access this ${experienceLabel.singular}.`}
            />
          }
          sublabel={
            <DiscoText variant={"body-sm"} color={"text.secondary"} display={"inline"}>
              {`Attach this ${experienceLabel.singular} to one or more existing membership plans. `}

              <DiscoLink to={generatePath(ROUTE_NAMES.ADMIN.PRODUCTS.MEMBERSHIPS.ROOT)}>
                <DiscoText
                  variant={"body-sm-700"}
                  display={"inline"}
                  color={"primary.main"}
                >
                  {"Manage Memberships"}
                </DiscoText>
              </DiscoLink>
            </DiscoText>
          }
        >
          {/* Membership Plans */}
          <DiscoSection className={classes.container}>
            {availableMembershipPlans.map((p, index) => (
              <Fragment key={p.id}>
                {index > 0 && <DiscoDivider marginTop={1.5} marginBottom={1.5} />}
                <MembershipPlanExperiencePricingListItem
                  form={form}
                  membershipPlanKey={p}
                  cta={
                    <div className={classes.cta}>
                      <DiscoChip
                        label={pluralize(
                          "Product",
                          p.membershipBenefits?.totalCount,
                          true
                        )}
                      />

                      <DiscoSwitch
                        checked={form.state.benefits.some(
                          (b) => b.membershipPlanId === p.id
                        )}
                        onChange={() => handleTogglePlan(p)}
                      />
                    </div>
                  }
                />
              </Fragment>
            ))}
          </DiscoSection>
        </DiscoFormControl>
      )}
    </>
  )

  function handleToggleIsPaid() {
    form.state.isPaid = !form.state.isPaid
  }

  function handleUpdateExtraCharge(
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) {
    form.state.price = decimalize(e.target.value)
  }

  function handleTogglePlan(plan: Plan) {
    const existingBenefit = existingBenefitsRef.current.find(
      (b) => b.membershipPlanId === plan.id
    )
    const isToggleOn = form.state.benefits.some((b) => b.membershipPlanId === plan.id)

    // If toggling off, remove the benefit
    if (isToggleOn) {
      form.state.benefits = observable.array(
        form.state.benefits.filter((b) => b.membershipPlanId !== plan.id)
      )
    }

    // If toggling on, add the benefit
    else if (existingBenefit) {
      // If benefit already exists, add it back
      form.state.benefits.push(existingBenefit)
    }

    // Create benefit with null ID so the resolver knows to create one
    else {
      form.state.benefits.push({
        id: null,
        membershipPlanId: plan.id,
        pricing: {
          amountCents: 0,
          kind: "free",
          frequency: null,
        },
      })
    }
  }
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    margin: theme.spacing(0.5, 0, 2, 0),
    display: "flex",
    flexDirection: "column",
    boxShadow: theme.palette.groovyDepths.insideCard,
    padding: theme.spacing(2),
  },
  extraCharge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: theme.palette.groovyDepths.insideCard,
    padding: theme.spacing(2),
  },
  cta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "space-between",
  },
}))

export const ExperiencePricingFormFieldsSkeleton = () => {
  const heights = [60]

  return (
    <div>
      {heights.map((height) => (
        <LabelFormFieldSkeleton key={height} height={height} width={350} />
      ))}
    </div>
  )
}

export default Relay.withSkeleton<Props>({
  component: observer(ExperiencePricingFormFields),
  skeleton: ExperiencePricingFormFieldsSkeleton,
})
